import type {
  AcceptOrgInvitationRequest,
  AcceptOrgInvitationResponse,
  ArchiveOrgUnitResponse,
  CreateOrgInvitationRequest,
  CreateOrgInvitationResponse,
  CreateOrgUnitRequest,
  CreateOrgUnitResponse,
  GetOrgInvitationResponse,
  ListOrgInvitationsResponse,
  ListOrgMembersResponse,
  ListOrgMemberModelPermissionsResponse,
  ListOrgUnitModelPermissionsResponse,
  ListOrgUnitsResponse,
  ListTenantAuditEventsResponse,
  OrgMemberRoleAssignment,
  ReplaceOrgMemberModelPermissionsRequest,
  ReplaceOrgMemberModelPermissionsResponse,
  ReplaceOrgUnitModelPermissionsRequest,
  ReplaceOrgUnitModelPermissionsResponse,
  RevokeOrgInvitationResponse,
  UpdateOrgMemberRequest,
  UpdateOrgMemberResponse,
  UpdateOrgUnitRequest,
  UpdateOrgUnitResponse,
} from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/orgs/*` + `/v1/invitations/*`
 * (PR-E.1 - N:1 org membership). See `apps/platform-api/src/routes/v1-orgs.ts`
 * for the endpoint definitions.
 */

export async function listOrgMembers(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgMembersResponse> {
  return platformApiRequest<ListOrgMembersResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/members`,
    { signal },
    fetchImpl,
  );
}

export async function listOrgUnits(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgUnitsResponse> {
  return platformApiRequest<ListOrgUnitsResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/org-units`,
    { signal },
    fetchImpl,
  );
}

export async function createOrgUnit(
  orgId: string,
  body: CreateOrgUnitRequest,
  fetchImpl: typeof fetch,
): Promise<CreateOrgUnitResponse> {
  return platformApiRequest<CreateOrgUnitResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/org-units`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function updateOrgUnit(
  orgId: string,
  orgUnitId: string,
  body: UpdateOrgUnitRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateOrgUnitResponse> {
  return platformApiRequest<UpdateOrgUnitResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/org-units/${encodeURIComponent(orgUnitId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
}

export async function archiveOrgUnit(
  orgId: string,
  orgUnitId: string,
  fetchImpl: typeof fetch,
): Promise<ArchiveOrgUnitResponse> {
  return platformApiRequest<ArchiveOrgUnitResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/org-units/${encodeURIComponent(orgUnitId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function updateOrgMember(
  orgId: string,
  accountId: string,
  body: UpdateOrgMemberRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateOrgMemberResponse> {
  return platformApiRequest<UpdateOrgMemberResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(accountId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
}

export async function assignOrgMemberRole(
  orgId: string,
  accountId: string,
  body: OrgMemberRoleAssignment,
  fetchImpl: typeof fetch,
): Promise<UpdateOrgMemberResponse> {
  return platformApiRequest<UpdateOrgMemberResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(accountId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
}

export async function listOrgInvitations(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgInvitationsResponse> {
  return platformApiRequest<ListOrgInvitationsResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/invitations`,
    { signal },
    fetchImpl,
  );
}

export interface ListTenantAuditEventsQuery {
  readonly limit?: number;
  readonly offset?: number;
  readonly action?: string;
  readonly targetAccountId?: string;
}

export async function listTenantAuditEvents(
  orgId: string,
  fetchImpl: typeof fetch,
  query?: ListTenantAuditEventsQuery,
  signal?: AbortSignal,
): Promise<ListTenantAuditEventsResponse> {
  const params = new URLSearchParams();
  if (query?.limit !== undefined) {
    params.set('limit', String(Math.max(1, Math.min(100, Math.floor(query.limit)))));
  }
  if (query?.offset !== undefined) {
    params.set('offset', String(Math.max(0, Math.floor(query.offset))));
  }
  const action = query?.action?.trim();
  if (action !== undefined && action.length > 0) {
    params.set('action', action);
  }
  const targetAccountId = query?.targetAccountId?.trim();
  if (targetAccountId !== undefined && targetAccountId.length > 0) {
    params.set('targetAccountId', targetAccountId);
  }
  const qs = params.toString();
  return platformApiRequest<ListTenantAuditEventsResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/audit-events${qs.length > 0 ? `?${qs}` : ''}`,
    { signal },
    fetchImpl,
  );
}

export async function listOrgMemberModelPermissions(
  orgId: string,
  accountId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgMemberModelPermissionsResponse> {
  return platformApiRequest<ListOrgMemberModelPermissionsResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(accountId)}/model-permissions`,
    { signal },
    fetchImpl,
  );
}

export async function listOrgUnitModelPermissions(
  orgId: string,
  orgUnitId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgUnitModelPermissionsResponse> {
  return platformApiRequest<ListOrgUnitModelPermissionsResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/org-units/${encodeURIComponent(orgUnitId)}/model-permissions`,
    { signal },
    fetchImpl,
  );
}

export async function replaceOrgMemberModelPermissions(
  orgId: string,
  accountId: string,
  body: ReplaceOrgMemberModelPermissionsRequest,
  fetchImpl: typeof fetch,
): Promise<ReplaceOrgMemberModelPermissionsResponse> {
  return platformApiRequest<ReplaceOrgMemberModelPermissionsResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(accountId)}/model-permissions`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function replaceOrgUnitModelPermissions(
  orgId: string,
  orgUnitId: string,
  body: ReplaceOrgUnitModelPermissionsRequest,
  fetchImpl: typeof fetch,
): Promise<ReplaceOrgUnitModelPermissionsResponse> {
  return platformApiRequest<ReplaceOrgUnitModelPermissionsResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/org-units/${encodeURIComponent(orgUnitId)}/model-permissions`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function createOrgInvitation(
  orgId: string,
  body: CreateOrgInvitationRequest,
  fetchImpl: typeof fetch,
): Promise<CreateOrgInvitationResponse> {
  return platformApiRequest<CreateOrgInvitationResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/invitations`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function revokeOrgInvitation(
  orgId: string,
  invitationId: string,
  fetchImpl: typeof fetch,
): Promise<RevokeOrgInvitationResponse> {
  return platformApiRequest<RevokeOrgInvitationResponse>(
    `/v1/orgs/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function getInvitation(
  token: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetOrgInvitationResponse> {
  return platformApiRequest<GetOrgInvitationResponse>(
    `/v1/invitations/${encodeURIComponent(token)}`,
    { signal },
    fetchImpl,
  );
}

export async function acceptInvitation(
  token: string,
  body: AcceptOrgInvitationRequest,
  fetchImpl: typeof fetch,
): Promise<AcceptOrgInvitationResponse> {
  return platformApiRequest<AcceptOrgInvitationResponse>(
    `/v1/invitations/${encodeURIComponent(token)}/accept`,
    { method: 'POST', body },
    fetchImpl,
  );
}
